import { z } from 'zod';
import { emailDeliveryStatuses, type EmailDeliveryStatus } from '../enums.js';
import { paginationFields, type PaginatedDto } from './_pagination.js';

/**
 * Console de plateforme — les services externes, et l'état du système.
 *
 * Ce que ces contrats décrivent, ce sont **nos** fournisseurs : ce qu'on leur envoie, ce qu'ils
 * nous coûtent, ce qu'ils répondent. Rien d'un cabinet n'y transite au-delà de son identifiant et
 * de son nom, et rien d'un patient n'y entre du tout — pas un corps de message, pas une invite,
 * pas un sujet libre, et le destinataire d'un courriel n'y figure que **masqué**.
 *
 * Trois de ces lectures dépendent d'une clé facultative. Aucune ne fait échouer l'écran quand la
 * clé manque : elles répondent « non configuré » et disent ce qu'elles savent quand même.
 */

/* -------------------------------------------------------------------------- */
/* Journal des envois de courriels                                             */
/* -------------------------------------------------------------------------- */

export const emailDeliveryListQuerySchema = z
  .object({
    status: z.enum(emailDeliveryStatuses).optional(),
    /** Nom de gabarit, `invitation`, `dunning`, `followUp`… Jamais un sujet. */
    template: z.string().trim().max(60).optional(),
    organizationId: z.uuid().optional(),
    /** Fenêtre, en jours écoulés. Sept par défaut : un journal d'envois se lit à chaud. */
    days: z.coerce.number().int().min(1).max(90).default(7),
    ...paginationFields,
  })
  .strict();
export type EmailDeliveryListQuery = z.infer<typeof emailDeliveryListQuerySchema>;

export interface EmailDeliveryDto {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  template: string;
  status: EmailDeliveryStatus;
  /**
   * Destinataire **masqué** : deux premiers caractères et domaine, `je***@cabinet.lu`. De quoi
   * répondre à « est-ce bien parti chez ce cabinet ? » sans conserver l'adresse d'un patient.
   */
  recipientMasked: string;
  providerMessageId: string | null;
  /** Cause technique, nom et code d'erreur. Jamais la réponse SMTP, qui recopie l'adresse. */
  error: string | null;
  createdAt: string;
}

export interface EmailDeliveryCountsDto {
  sent: number;
  failed: number;
}

export interface EmailDeliveryListDto extends PaginatedDto<EmailDeliveryDto> {
  /** Comptes sur la fenêtre demandée, avant pagination : c'est le chiffre qui alerte. */
  counts: EmailDeliveryCountsDto;
}

/* -------------------------------------------------------------------------- */
/* Fournisseur d'envoi                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Les deux sources, **côte à côte et non fondues**, parce qu'elles ne disent pas la même chose.
 * Notre journal dit ce que le transport a **accepté** ; le fournisseur dit ce qu'il a réellement
 * **délivré**. SMTP ne nous rapporte ni un rebond ni un classement en indésirable : les additionner
 * fabriquerait un chiffre qui n'existe nulle part.
 */

/**
 * Cadence de la tâche `send-appointment-reminders`, lue sur la tâche elle-même.
 * `daily` : une fois par jour à `hour`:`minute`, heure murale de `timeZone`.
 * `every` : toutes les `everyMinutes` minutes.
 */
export interface AppointmentReminderCadenceDto {
  kind: 'daily' | 'every';
  hour: number;
  minute: number;
  everyMinutes: number;
  timeZone: string;
}

export interface PlatformEmailProviderDto {
  /** Notre journal, sur la fenêtre. Toujours présent : il ne dépend d'aucune clé. */
  log: EmailDeliveryCountsDto;
  /** `false` sans `MAILTRAP_API_TOKEN` : `provider` est alors nul, et c'est une réponse complète. */
  configured: boolean;
  provider: {
    name: string;
    sent: number;
    /** Messages refusés par le serveur du destinataire. Invisibles de notre côté. */
    bounced: number;
    /** Messages classés en indésirable par le destinataire. Invisibles de notre côté. */
    spam: number;
  } | null;
  /** Renseigné quand l'appel au fournisseur a échoué : la console le dit au lieu d'afficher zéro. */
  providerError: string | null;
  /** Rythme d'envoi des rappels de rendez-vous. Ni destinataire, ni texte. */
  appointmentReminders: AppointmentReminderCadenceDto;
}

/* -------------------------------------------------------------------------- */
/* Usage et coût de l'IA                                                       */
/* -------------------------------------------------------------------------- */

export const aiUsageQuerySchema = z
  .object({
    /** Nombre de mois civils rendus, le mois en cours inclus. */
    months: z.coerce.number().int().min(1).max(24).default(6),
    organizationId: z.uuid().optional(),
  })
  .strict();
export type AiUsageQuery = z.infer<typeof aiUsageQuerySchema>;

export interface AiUsageBucketDto {
  /** Mois civil du cabinet, `YYYY-MM`. */
  month: string;
  generations: number;
  inputTokens: number;
  outputTokens: number;
  /** Durée moyenne d'un appel, en millisecondes. Zéro quand le mois est vide. */
  averageDurationMs: number;
}

export interface AiUsageByOrganizationDto {
  organizationId: string;
  organizationName: string | null;
  generations: number;
  inputTokens: number;
  outputTokens: number;
}

export interface PlatformAiUsageDto {
  /** Par mois, du plus ancien au plus récent, les mois vides compris. */
  months: AiUsageBucketDto[];
  /** Par cabinet, sur toute la fenêtre, les plus gros consommateurs d'abord. */
  organizations: AiUsageByOrganizationDto[];
  /** Modèle le plus servi sur la fenêtre. Nul si rien n'a été généré. */
  model: string | null;
}

/* -------------------------------------------------------------------------- */
/* Fournisseur de rédaction : état, et essai de la clé                         */
/* -------------------------------------------------------------------------- */

/** Les fournisseurs possibles. Un seul à la fois, désigné par la clé posée sur le serveur. */
export const aiProviders = ['anthropic', 'openai'] as const;
export type AiProviderName = (typeof aiProviders)[number];

/**
 * Ce que la console sait du fournisseur de rédaction.
 *
 * **Aucune clé, ni un fragment de clé, ne figure ici**, et ce n'est pas une précaution de style :
 * la V1 stockait la clé OpenAI en base et la renvoyait à l'écran d'administration, ce que l'audit
 * a qualifié de fuite. La console dit qu'une clé est posée, elle ne la montre pas, et il n'y a
 * pas de route pour l'écrire — elle se change dans l'environnement du serveur.
 */
export interface PlatformAiStatusDto {
  /** Le fournisseur en service, ou `null` si aucune clé n'est posée. */
  provider: AiProviderName | null;
  /** Vrai dès qu'une clé est posée, donc dès que la génération de courriers est possible. */
  configured: boolean;
  /** Modèle employé : `AI_MODEL` si elle est posée, sinon le défaut du fournisseur. */
  model: string;
  /**
   * La carte de coût est-elle servie ? Elle vient de la facturation d'Anthropic et n'a pas
   * d'équivalent branché pour OpenAI — on préfère le dire à estimer un montant maison.
   */
  costAvailable: boolean;
  /**
   * La variable à poser quand rien ne l'est. Deux noms possibles, au choix de l'exploitant, et
   * `null` dès qu'une clé est là.
   */
  missingVariables: string[];
}

/** Résultat d'un essai de la clé : ce qui s'est passé, jamais ce que le fournisseur a écrit. */
export interface PlatformAiTestDto {
  ok: boolean;
  provider: AiProviderName;
  model: string;
  /** Durée de l'aller-retour, mesurée chez nous. Utile pour distinguer lent de cassé. */
  durationMs: number;
  /**
   * La réponse a été coupée par le plafond de sortie. Sur un essai, cela veut dire que le budget
   * est trop serré pour ce modèle — un modèle à raisonnement dépense d'abord en réflexion.
   */
  truncated: boolean;
  /** Message d'erreur **normalisé** par nos soins, ou nul. Jamais la réponse du fournisseur. */
  error: string | null;
}

export const aiCostQuerySchema = z
  .object({ days: z.coerce.number().int().min(1).max(31).default(30) })
  .strict();
export type AiCostQuery = z.infer<typeof aiCostQuerySchema>;

export interface AiCostBucketDto {
  /** Jour civil `YYYY-MM-DD`, tel qu'Anthropic le découpe (UTC). */
  date: string;
  /** Coût du jour en **centimes**, comme l'API le rend, et jamais un flottant d'euros. */
  amountCents: number;
}

/**
 * Le coût facturé par Anthropic. **C'est leur chiffre, pas le nôtre** : nous comptons des jetons,
 * eux facturent — recalculer un montant depuis nos compteurs donnerait un total qui ne coïnciderait
 * avec aucune facture.
 */
export interface PlatformAiCostDto {
  /** Devise du rapport. `USD` aujourd'hui, et c'est l'API qui le dit. */
  currency: string;
  totalCents: number;
  days: AiCostBucketDto[];
}

/* -------------------------------------------------------------------------- */
/* Système                                                                     */
/* -------------------------------------------------------------------------- */

/** Une recherche d'adresse aboutie, rattachée à un membre et à son cabinet. Jamais la saisie. */
export interface AddressLookupMemberUsageDto {
  userId: string;
  /** Prénom et nom, ou `null` si le compte a été effacé. */
  memberName: string | null;
  organizationId: string;
  /** Nom du cabinet, ou `null` s'il a été effacé. */
  organizationName: string | null;
  /** Recherches abouties depuis minuit, heure du Luxembourg. */
  today: number;
  /** Recherches abouties sur les sept derniers jours, aujourd'hui compris. */
  last7Days: number;
}

/**
 * La passerelle d'adresses, vue de la console.
 *
 * **La clé entière n'y figure pas.** Quatre caractères de fin, et seulement pour un super
 * administrateur, suffisent à reconnaître celle qui est posée. L'écrire ou la relire en entier
 * depuis un navigateur est la fuite que la V1 a faite avec la clé OpenAI.
 */
export interface PlatformAddressLookupDto {
  configured: boolean;
  /** Quatre derniers caractères, ou `null` pour qui n'est pas super administrateur, ou si absente. */
  keyLast4: string | null;
  /** Plafond partagé du jour. Informatif : l'écran ne le modifie pas. */
  dailyQuota: number;
  today: number;
  last7Days: number;
  members: AddressLookupMemberUsageDto[];
}

export interface PlatformSystemDto {
  /** `APP_VERSION`, ou « inconnue » : la console ne devine pas un numéro. */
  version: string;
  /** Octets occupés par la base. Ce que le disque paiera. */
  databaseBytes: number;
  /** Octets de documents vivants, tous cabinets confondus. */
  storageBytes: number;
  /** Tâches planifiées en échec sur vingt-quatre heures. Zéro est l'état normal. */
  failedJobs24h: number;
  /** Depuis combien de temps ce processus tourne, en secondes. */
  uptimeSeconds: number;
  /** Version de PostgreSQL, telle que le serveur se nomme. */
  databaseVersion: string;
  startedAt: string;
}
